// Libraries
import moment from 'moment';
import React from 'react';
import BigCalendar from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Supermove
import {Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {usePagination, useState} from '@supermove/hooks';
import {UserCalendarEventModel} from '@supermove/models';

// App
import PaginationBar from '@shared/design/components/Pagination/PaginationBar';
import ErrorCatcher from '@shared/modules/Error/components/ErrorCatcher';
import {
  toBigCalendarView,
  toUserCalendarViewType,
  USER_CALENDAR_VIEW_TYPE,
} from '@shared/modules/UserCalendar/enums/UserCalendarViewType';

import './UserCalendar.css';

export type UserCalendarSource = {
  userId: string;
  calendarId: string;
  title: string;
};

const localizer = BigCalendar.momentLocalizer(moment);

const Column = Styled.View`
  flex: 1;
`;

const UserCalendar = ({
  events,
  calendars,
  calendarsPerPage = 2,
  startDate,
  viewType,
  views,
  hideToolbar = false,
  scrollToTime = new Date(new Date().setHours(9, 0, 0, 0)),
  handleDateChange,
  handleViewChange,
  handleSelectSlot,
}: {
  events: UserCalendarEventModel[];
  calendars?: UserCalendarSource[];
  calendarsPerPage?: number;
  startDate: Date;
  viewType: USER_CALENDAR_VIEW_TYPE;
  views: USER_CALENDAR_VIEW_TYPE[];
  hideToolbar?: boolean;
  scrollToTime?: Date;
  handleDateChange?: (baseDate: Date) => void;
  handleViewChange?: (viewType: USER_CALENDAR_VIEW_TYPE) => void;
  handleSelectSlot?: (slotInfo: {
    start: Date | string;
    end: Date | string;
    action: 'select' | 'click' | 'doubleClick';
  }) => void;
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = calendars ? Math.ceil(calendars.length / calendarsPerPage) : 1;
  const pagination = usePagination({
    currentPage,
    onChangePage: (page: number) => setCurrentPage(page),
    paginationMetadata: {
      totalPages,
      hasPrevious: currentPage !== 1,
      hasNext: currentPage !== totalPages,
    },
  });

  const shownCalendars = calendars?.slice(
    (currentPage - 1) * calendarsPerPage,
    currentPage * calendarsPerPage,
  );

  const calendarEvents = events.map((event) => ({
    id: event.id,
    title: event.title,
    start: new Date(event.startTime * 1000),
    end: new Date(event.endTime * 1000),
    allDay: event.whenType === 'DATE',
    resourceId: event.calendarId,
  }));

  const handleNavigate = (date: Date) => {
    handleDateChange?.(date);
  };

  const allViews = views.map(toBigCalendarView);
  const currentView = toBigCalendarView(viewType);

  const resourceProps = calendars
    ? {
        resources: shownCalendars,
        resourceIdAccessor: 'calendarId' as keyof UserCalendarSource,
        resourceTitleAccessor: 'title' as keyof UserCalendarSource,
      }
    : {};

  return (
    <Column>
      <Column>
        <ErrorCatcher>
          <BigCalendar
            toolbar={!hideToolbar}
            localizer={localizer}
            events={calendarEvents}
            startAccessor='start'
            endAccessor='end'
            date={startDate}
            view={currentView}
            views={allViews}
            onView={(view) => handleViewChange?.(toUserCalendarViewType(view))}
            onNavigate={handleNavigate}
            onSelectSlot={handleSelectSlot}
            selectable={false}
            scrollToTime={scrollToTime}
            {...resourceProps}
          />
        </ErrorCatcher>
      </Column>
      {totalPages > 1 && (
        <React.Fragment>
          <Space height={8} />
          <PaginationBar pagination={pagination} />
        </React.Fragment>
      )}
    </Column>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
UserCalendar.fragment = gql`
  fragment UserCalendar on User {
    id
    calendarEvents(calendarId: $calendarId, startDate: $startDate, endDate: $endDate) {
      id
      calendarId
      busy
      description
      textDescription
      htmlLink
      readOnly
      status
      title
      whenType
      startTime
      endTime
      dateValue
      startDate
      endDate
    }
  }
`;

export default UserCalendar;
